import React from "react";
import {
  Col,
  FormGroup,
  Input,
  Row,
} from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import { serviceCaller, ServiceCallerProps } from "../../composition";

import isNil from "lodash.isnil";
import isEmpty from "lodash.isempty";
import isEqual from "lodash.isequal";
import get from "lodash.get";
import { IPost, PostType, PostStyle } from "../../../types/services/IPost";
import PostEditor from "../postEditor";
import { isCurrentUserCollaborator } from "../../../utils/roleCheck";
import s from "./styles.scss";
import { postOnlySMS } from "../../../utils/helpers";
import ReactTooltip from "react-tooltip";
import {
  searchDonationById,
  searchDonorById,
  subjectLength,
  titleLenght,
} from "../../../utils/fetch";
import EnableSalutationMessageCheckbox from "../postDetail/enableSalutationMessageCheckbox";

interface Props extends ServiceCallerProps {
  currentPost: {
    post: IPost;
    type: PostType;
  };

  onlySMS: PostStyle;
  busyLoading: boolean;
  disabled: boolean;
  thanksDonationIds: Array<string>;
  thanksDonorSegments?: Array<{ label: string; value: string }>;
  updateBasicInfo: (fields: { [s: string]: any }) => void;
  enableSalutationFeature: boolean;
  updateEnableSalutationMessage: (event) => void;
  enableSalutationMessage: boolean;
}

interface State {
  fields: {
    title: string;
    content: string;
    styledContent: string;
  };
  contentCount: number;
  titleCount: number;
  contentSaved: string;
  messageSelectionEnd: number;
  customTitleMaxLength: number;
  maxContentByStyle: {
    news: number;
    informative: number;
    onlySms: number;
    onlyMms: number;
  };
}

const maxTitleLength = 104;

class PostBasicInfo extends React.PureComponent<Props, State> {
  static defaultProps = {
    onlySMS: "news",
    disabled: false,
    enableSalutationMessage: false,
  };

  input = null;
  constructor(props: Props) {
    super(props);
    const post = get(this.props.currentPost, "post", null);
    const content = get(post, "content", "");
    this.state = {
      fields: {
        title: get(post, "title", ""),
        content,
        styledContent: get(post, "styledContent", ""),
      },
      contentCount: !isNil(post) ? (post.content ? post.content.length : 0) : 0,
      titleCount: !isNil(post) ? (post.title ? post.title.length : 0) : 0,
      contentSaved: get(post, "content", ""),
      messageSelectionEnd: 0,
      customTitleMaxLength: maxTitleLength,
      maxContentByStyle: {
        news: 1000,
        informative: 1000,
        onlySms: 1547,
        onlyMms: 1547,
      },
    };
  }

  getMaxContentLimit = (postType: PostStyle): number => {
    const { maxContentByStyle } = this.state;
    if (postOnlySMS(postType)) {
      return maxContentByStyle.onlyMms;
    }
    return maxContentByStyle[postType];
  };

  getBasicErrors = (): any => {
    let errors = [];
    const maxContent = this.getMaxContentLimit(this.props.onlySMS);
    if (this.state.titleCount > this.state.customTitleMaxLength) {
      errors.push(
        `Must be less than ${this.state.customTitleMaxLength} characters long`
      );
    }
    if (this.state.contentCount > maxContent) {
      errors.push(`Must be less than ${maxContent} characters long`);
    }
    return errors;
  };

  applyThanksPrefill = async () => {
    const { thanksDonationIds, thanksDonorSegments, config } = this.props;
    const hasThanksInfo =
      (!isNil(thanksDonationIds) && !isEmpty(thanksDonationIds)) ||
      (!isNil(thanksDonorSegments) && !isEmpty(thanksDonorSegments));
    if (!hasThanksInfo || isNil(config)) {
      return;
    }

    let content = "",
      title = "";

    if (thanksDonationIds && thanksDonationIds.length) {
      if (thanksDonationIds.length > 1) {
        title = 'I wanted to say a quick "thank you" for...';
        content =
          "... the gift you just gave. Take a listen to this short video I just recorded for you.\n\n" +
          "We are so thankful for your support of our work.";
      } else {
        const donationId = thanksDonationIds[0];

        const donation = await searchDonationById(
          config.adminServiceUrl,
          this.props.currentCharity.id,
          donationId,
          this.props.serviceAccessToken
        );
        const donor = await searchDonorById(
          config.adminServiceUrl,
          this.props.currentCharity.id,
          donation.donorId,
          this.props.serviceAccessToken
        );

        title = `${donor.firstName}, I wanted to say a quick...`;
        content =
          `... "thank you" for the gift of ${donation.amountPretty} you just gave. Take a listen to this short video I just recorded for you.\n\n` +
          "We are so thankful for your support of our work.";
      }
    } else if (thanksDonorSegments && thanksDonorSegments.length) {
      if (thanksDonorSegments.length > 1) {
        content =
          "Take a listen to this short video I just recorded for you.\n\n" +
          "We are so thankful for your support of our work.";
      } else {
        const donor = await searchDonorById(
          config.adminServiceUrl,
          this.props.currentCharity.id,
          thanksDonorSegments[0].value.slice(1),
          this.props.serviceAccessToken
        );
        content =
          `Dear ${donor.firstName}, take a listen to this short video I just recorded for you.\n\n` +
          "We are so thankful for your support of our work.";
      }
    }

    if (content || title) {
      this.setState(
        {
          fields: {
            ...this.state.fields,
            content,
            title,
            styledContent: this.getStyledContent(content),
          },
        },
        () => this.props.updateBasicInfo(this.state.fields)
      );
    }
  };

  async componentDidUpdate(prevProps: Props) {
    if (!isEqual(prevProps.onlySMS, this.props.onlySMS)) {
      this.props.updateBasicInfo({
        basicErrors: this.getBasicErrors(),
      });
    }

    if (
      !isNil(this.props.config) &&
      !isEqual(prevProps.config, this.props.config)
    ) {
      this.getSubjectLimit();
      this.getTitleLimit();
    }

    const { thanksDonationIds, thanksDonorSegments } = this.props;
    const hasThanksInfo =
      (!isNil(thanksDonationIds) && !isEmpty(thanksDonationIds)) ||
      (!isNil(thanksDonorSegments) && !isEmpty(thanksDonorSegments));
    if (
      hasThanksInfo &&
      (thanksDonationIds !== prevProps.thanksDonationIds ||
        thanksDonorSegments !== prevProps.thanksDonorSegments)
    ) {
      await this.applyThanksPrefill();
    }
  }

  componentDidMount() {
    if (!isNil(this.props.currentPost)) {
      this.props.updateBasicInfo({
        styledContent: this.getStyledContent(
          this.props.currentPost.post.styledContent
        ),
        title: this.state.fields.title,
        content: this.state.fields.content,
      });
    }

    if (!isNil(this.props.config)) {
      this.getSubjectLimit();
      this.getTitleLimit();
    }

    void this.applyThanksPrefill();
  }

  getSubjectLimit = async (): Promise<void> => {
    try {
      const response = await subjectLength(
        this.props.config.adminServiceUrl,
        this.props.currentCharity.id,
        this.props.serviceAccessToken
      );

      const onlyMmsLimit = response.onlyMms ?? response.onlySms;
      this.setState({
        maxContentByStyle: {
          ...response,
          onlySms: onlyMmsLimit,
          onlyMms: onlyMmsLimit,
        },
      });
    } catch (e) {
      console.warn("subjectLength request failed", e);
    }
  };

  getTitleLimit = async (): Promise<void> => {
    try {
      const response = await titleLenght(
        this.props.config.adminServiceUrl,
        this.props.currentCharity.id,
        this.props.serviceAccessToken
      );

      this.setState({
        customTitleMaxLength:
          !this.props.currentPost || this.props.currentPost.type === "pending"
            ? response["length"]
            : 104,
      });
    } catch (e) {
      console.warn("titleLength request failed", e);
    }
  };

  /*
  fillPostData = () => {
    const { post } = this.props.currentPost;
    const url = get(post, "onlySmsUrl", "");
    this.setState({
      fields: {
        title: post.title,
        content: post.content,
        styledContent: post.styledContent,
        onlySmsUrl: url,
      },
      selectedHttpsFormat: this.getHttpFormat(url),
      contentCount: post.content ? post.content.length : 0,
      titleCount: post.title ? post.title.length : 0,
    });
  };
  */

  uppercaseFirstLetter = text => {
    return text.charAt(0).toUpperCase() + text.substr(1);
  };

  postContentChanged = (content: string, styledContent: string) => {
    this.setState(
      {
        contentCount: content.length,
        contentSaved: content,
      },
      () => {
        this.props.updateBasicInfo({
          basicErrors: this.getBasicErrors(),
          content,
          styledContent: this.getStyledContent(styledContent),
        });
      }
    );
  };

  getStyledContent = (styledContent: string): string => {
    if (isNil(styledContent) || isEmpty(styledContent)) {
      return null;
    }

    const matched = styledContent.match(
      /<\/strong>|<\/em>|<\/u>|<\/del>|<\/ul>|<\/ol>|<\/li>|<\/a>/
    );
    if (
      isNil(matched) ||
      matched.length === 0 ||
      this.state.contentCount === 0
    ) {
      return null;
    }

    return styledContent;
  };

  onValidation = fieldName => {
    return event => {
      const value = this.uppercaseFirstLetter(event.target.value);

      this.setState(
        state => {
          const fields = {
            ...state.fields,
            [fieldName]: value,
          };
          const titleCounts =
            fieldName === "title" ? { titleCount: value.length } : {};

          return {
            ...state,
            fields,
            ...titleCounts,
          };
        },
        () => {
          this.props.updateBasicInfo({
            basicErrors: this.getBasicErrors(),
            [fieldName]: value,
          });
        }
      );
    };
  };

  getParsedNormalContent = (content: string): string => {
    content = "<p>" + content;
    content += "</p>";
    content = content.replace(/\n/g, "</p><p>");
    return content;
  };

  // ***** Insert Donor Name - On HOLD ***** //
  /*

  updateCursorPosition = event => {
    const position = event.target.selectionEnd;
    this.setState({ messageSelectionEnd: position });
  };

  insertDonorNameKey = () => {
    const { messageSelectionEnd, fields } = this.state;
    var messageField = fields.content;
    var donorName = " @donor ";
    var position = messageSelectionEnd;
    var value = [
      messageField.slice(0, position),
      donorName,
      messageField.slice(position),
    ].join("");

    const newPosition = this.state.messageSelectionEnd + donorName.length;

    this.setState(
      state => {
        const fields = {
          ...state.fields,
          content: value,
        };
        this.re.validateField("content", value);
        const counts = { contentCount: value.length };

        return {
          ...state,
          error: {},
          fields,
          ...counts,
          messageSelectionEnd: newPosition,
        };
      },
      () => {
        this.restoreSelection();
      }
    );
  };

  renderInsertDonorName = (): JSX.Element => {
    return (
       <Fragment>
         <Button
           id={"insertDonorNameKey"}
           onClick={this.insertDonorNameKey}
           className={s.buttonSmall}
           tabIndex="0"
           data-tip
           data-for={`tpinsertDonorNameKey`}
         >
           <i className={"fa fa-address-card"} />
         </Button>
         <ReactTooltip
           id={`tpinsertDonorNameKey`}
           type="info"
           place="top"
           effect="float"
         >
           Use this button to insert the first name of the
           donors
         </ReactTooltip>
       </Fragment>
    )
  };*/
  //////////

  render() {
    const { busyLoading, onlySMS, disabled } = this.props;
    const { content, styledContent } = this.state.fields;
    const { contentCount, customTitleMaxLength } = this.state;
    const maxContent = this.getMaxContentLimit(onlySMS);
    let initialContent: string = undefined;

    if (styledContent || content) {
      initialContent = !isEmpty(styledContent)
        ? styledContent
        : this.getParsedNormalContent(content);
    }

    const messageProps = {
      style: { height: "calc(100% - 140px)" },
    };

    return (
      <Col md={12} sm={12} lg={12} xl={5}>
        <Row>
          <Col sm={12}>
            <FormGroup>
              <label className={s.titleLabel} htmlFor="title">
                * Subject
                <span
                  className={
                    this.state.titleCount > customTitleMaxLength
                      ? s.invalidContentLength
                      : s.contentLength
                  }
                >
                  {this.state.titleCount} / {customTitleMaxLength}
                </span>
              </label>

              <Input
                type="text"
                name="title"
                id="title"
                placeholder="Add ViewSpark Subject"
                value={this.state.fields.title}
                onChange={this.onValidation("title")}
                disabled={
                  busyLoading ||
                  !isCurrentUserCollaborator(this.props.currentCharity) ||
                  disabled
                }
              />
            </FormGroup>
          </Col>
        </Row>
        <Row {...messageProps}>
          <Col sm={12} className="h-100">
            <FormGroup className="h-100">
              {this.props.enableSalutationFeature && (
                <EnableSalutationMessageCheckbox
                  enableSalutationMessage={this.props.enableSalutationMessage}
                  updateEnableSalutationMessage={
                    this.props.updateEnableSalutationMessage
                  }
                />
              )}
              <label className={s.titleLabel} htmlFor="content">
                * Add Message Content
                {/* this.renderInsertDonorName() */}
                <span
                  className={
                    contentCount > maxContent
                      ? s.invalidContentLength
                      : s.contentLength
                  }
                >
                  {contentCount} / {maxContent}
                </span>
              </label>

              <PostEditor
                id="content"
                className="h-100"
                readonly={
                  !isNil(busyLoading) ||
                  !isCurrentUserCollaborator(this.props.currentCharity)
                }
                justScroll={disabled}
                onlySMS={false}
                initialStyledContent={initialContent}
                onEditorChange={this.postContentChanged}
              />
            </FormGroup>
          </Col>
        </Row>
      </Col>
    );
  }
}

export default serviceCaller(withStyles(s)(PostBasicInfo));
